import {
  Divider,
  FormControlLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
} from '@mui/material';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { ENGLISH_FONTS, KOREAN_FONTS, LIMITS, fontWeightsFor } from '../constants';
import type { Dictionary } from '../i18n';
import type { TextAlign, TextLayer } from '../types';
import { ColorField, SliderField } from './fields';

interface Props {
  t: Dictionary;
  layer: TextLayer;
  onChange: (patch: Partial<TextLayer>, tag?: string) => void;
}

export function TextLayerControls({ t, layer, onChange }: Props) {
  const weights = fontWeightsFor(layer.fontFamily);
  const weightLabel = (weight: number) =>
    weight >= 900 ? t.weightBlack : weight >= 700 ? t.weightBold : t.weightRegular;

  return (
    <Stack spacing={2}>
      <FormControl fullWidth>
        <InputLabel id={`font-${layer.fontFamily}`}>{t.font}</InputLabel>
        <Select
          labelId={`font-${layer.fontFamily}`}
          label={t.font}
          value={layer.fontFamily}
          onChange={(event) => {
            const family = event.target.value;
            // Keep the weight within what the newly chosen webfont actually ships.
            const available = fontWeightsFor(family);
            const nearest = available.reduce((best, w) =>
              Math.abs(w - layer.fontWeight) < Math.abs(best - layer.fontWeight) ? w : best,
            );
            onChange({ fontFamily: family, fontWeight: nearest });
          }}
        >
          <ListSubheader>{t.koreanFonts}</ListSubheader>
          {KOREAN_FONTS.map((font) => (
            <MenuItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
              {font.label}
            </MenuItem>
          ))}
          <ListSubheader>{t.englishFonts}</ListSubheader>
          {ENGLISH_FONTS.map((font) => (
            <MenuItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
              {font.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={layer.fontWeight}
          onChange={(_, value: number | null) => value !== null && onChange({ fontWeight: value })}
          aria-label={t.weight}
        >
          {weights.map((weight) => (
            <ToggleButton key={weight} value={weight} sx={{ fontWeight: weight, px: 1.5 }}>
              {weightLabel(weight)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={layer.align}
          onChange={(_, value: TextAlign | null) => value !== null && onChange({ align: value })}
          aria-label={t.align}
        >
          <ToggleButton value="left" aria-label={t.alignLeft}>
            <FormatAlignLeftIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="center" aria-label={t.alignCenter}>
            <FormatAlignCenterIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="right" aria-label={t.alignRight}>
            <FormatAlignRightIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <SliderField
        label={t.size}
        unit="px"
        value={layer.fontSize}
        min={LIMITS.fontSize.min}
        max={LIMITS.fontSize.max}
        onChange={(value) => onChange({ fontSize: value }, 'fontSize')}
      />
      <ColorField
        label={t.color}
        value={layer.color}
        onChange={(value) => onChange({ color: value }, 'color')}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SliderField
          label={t.lineHeight}
          value={layer.lineHeight}
          min={LIMITS.lineHeight.min}
          max={LIMITS.lineHeight.max}
          step={0.05}
          onChange={(value) => onChange({ lineHeight: value }, 'lineHeight')}
        />
        <SliderField
          label={t.letterSpacing}
          unit="px"
          value={layer.letterSpacing}
          min={LIMITS.letterSpacing.min}
          max={LIMITS.letterSpacing.max}
          onChange={(value) => onChange({ letterSpacing: value }, 'letterSpacing')}
        />
      </Stack>

      <Divider />

      <FormControlLabel
        control={
          <Switch
            checked={layer.strokeEnabled}
            onChange={(event) => onChange({ strokeEnabled: event.target.checked })}
          />
        }
        label={t.strokeEnabled}
      />
      {layer.strokeEnabled && (
        <Stack spacing={2} sx={{ pl: 1 }}>
          <SliderField
            label={t.thickness}
            unit="px"
            value={layer.strokeWidth}
            min={LIMITS.strokeWidth.min}
            max={LIMITS.strokeWidth.max}
            onChange={(value) => onChange({ strokeWidth: value }, 'strokeWidth')}
          />
          <ColorField
            label={t.color}
            value={layer.strokeColor}
            onChange={(value) => onChange({ strokeColor: value }, 'strokeColor')}
          />
        </Stack>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={layer.shadowEnabled}
            onChange={(event) => onChange({ shadowEnabled: event.target.checked })}
          />
        }
        label={t.shadowEnabled}
      />
      {layer.shadowEnabled && (
        <Stack spacing={2} sx={{ pl: 1 }}>
          <SliderField
            label={t.softness}
            unit="px"
            value={layer.shadowBlur}
            min={0}
            max={120}
            onChange={(value) => onChange({ shadowBlur: value }, 'shadowBlur')}
          />
          <ColorField
            label={t.color}
            value={layer.shadowColor}
            onChange={(value) => onChange({ shadowColor: value }, 'shadowColor')}
          />
        </Stack>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={layer.backgroundEnabled}
            onChange={(event) => onChange({ backgroundEnabled: event.target.checked })}
          />
        }
        label={t.backgroundEnabled}
      />
      {layer.backgroundEnabled && (
        <Stack spacing={2} sx={{ pl: 1 }}>
          <ColorField
            label={t.color}
            value={layer.backgroundColor}
            onChange={(value) => onChange({ backgroundColor: value }, 'bgColor')}
          />
          <SliderField
            label={t.opacity}
            unit="%"
            value={layer.backgroundOpacity}
            min={0}
            max={100}
            onChange={(value) => onChange({ backgroundOpacity: value }, 'bgOpacity')}
          />
          <SliderField
            label={t.padding}
            unit="px"
            value={layer.backgroundPadding}
            min={LIMITS.padding.min}
            max={LIMITS.padding.max}
            onChange={(value) => onChange({ backgroundPadding: value }, 'bgPadding')}
          />
          <SliderField
            label={t.round}
            unit="px"
            value={layer.backgroundRadius}
            min={0}
            max={80}
            onChange={(value) => onChange({ backgroundRadius: value }, 'bgRadius')}
          />
        </Stack>
      )}
    </Stack>
  );
}
